import { useMemo } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { useApolloClients } from "~/components/ApolloClients";
import {
  GET_QUESTION_BANK,
  CREATE_BANK_QUESTION,
  UPDATE_BANK_QUESTION,
  DELETE_BANK_QUESTION,
} from "./operations";
import type {
  BankQuestionDisplay,
  QuestionBankFilter,
  GuideQuestionInput,
  BankQuestionUpdateInput,
} from "../types";
import { toBankQuestionDisplay } from "../mappers/questionBankMapper";
import { executeMutation } from "./mutationHelpers";

export interface QuestionBankPageInfo {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export function useQuestionBank(
  filter: QuestionBankFilter,
  /**
   * Server-side pagination. When omitted, fetch a large single page so
   * non-paginated consumers see the whole library (the backend otherwise
   * returns a small default page).
   */
  pagination?: { page: number; pageSize: number },
  /** `skip: true` suppresses the query — e.g. a picker dialog while closed. */
  options?: { skip?: boolean },
) {
  const { federationGatewayClient } = useApolloClients();

  const { data, previousData, loading, error, refetch } = useQuery(
    GET_QUESTION_BANK,
    {
      client: federationGatewayClient,
      variables: {
        filter: {
          search: filter.search,
          kssartiTopic: filter.kssartiTopic,
          division: filter.division,
          jobTitle: filter.jobTitle,
        },
        pagination: pagination ?? { page: 1, pageSize: 500 },
      },
      fetchPolicy: "cache-and-network",
      skip: options?.skip,
    },
  );

  // Changing page/filter makes `data` undefined while the new variables load;
  // fall back to the previous result so questions/pageInfo don't collapse
  // mid-transition (which would bounce the pager back).
  const effective = data ?? previousData;

  const questions: BankQuestionDisplay[] = useMemo(
    () => (effective?.questionBank?.items || []).map(toBankQuestionDisplay),
    [effective?.questionBank?.items],
  );

  const pageInfo: QuestionBankPageInfo = effective?.questionBank?.pageInfo ?? {
    page: pagination?.page ?? 1,
    pageSize: pagination?.pageSize ?? 500,
    totalItems: questions.length,
    totalPages: 1,
  };

  return { questions, pageInfo, loading, error, refetch };
}

export function useQuestionBankMutations() {
  const { federationGatewayClient } = useApolloClients();

  // create / update / delete can all affect which questions appear in the
  // currently filtered list (e.g. updating `division`, `jobTitle`, or
  // `kssartiTopic` changes filter membership), so refetch the active
  // `GetQuestionBank` observable by name. Refetching by name re-uses the
  // current variables (including the active filter) instead of issuing a
  // separate unfiltered query.
  const refetchBank = {
    refetchQueries: ["GetQuestionBank"],
    awaitRefetchQueries: false,
  };

  const [createBankQuestion] = useMutation(CREATE_BANK_QUESTION, {
    client: federationGatewayClient,
    ...refetchBank,
  });
  const [updateBankQuestion] = useMutation(UPDATE_BANK_QUESTION, {
    client: federationGatewayClient,
    ...refetchBank,
  });
  const [deleteBankQuestion] = useMutation(DELETE_BANK_QUESTION, {
    client: federationGatewayClient,
    ...refetchBank,
  });

  return {
    createBankQuestion: async (
      input: GuideQuestionInput & { division?: string; jobTitle?: string },
    ) => {
      const result = await executeMutation(
        "createBankQuestion",
        () =>
          createBankQuestion({
            variables: {
              input: {
                text: input.text,
                scoringGuide: input.scoringGuide,
                kssartiTopic: input.kssartiTopic,
                division: input.division,
                jobTitle: input.jobTitle,
              },
            },
          }),
        (d: any) => d.bankQuestionCreate,
      );
      return result;
    },
    updateBankQuestion: async (
      questionId: string,
      input: BankQuestionUpdateInput,
    ) => {
      const result = await executeMutation(
        "updateBankQuestion",
        () => updateBankQuestion({ variables: { id: questionId, input } }),
        (d: any) => d.bankQuestionUpdate,
      );
      return result;
    },
    deleteBankQuestion: async (questionId: string) => {
      const result = await executeMutation(
        "deleteBankQuestion",
        () => deleteBankQuestion({ variables: { id: questionId } }),
        (d: any) => d.bankQuestionDelete,
      );
      return result;
    },
  };
}
